/**
 * Validadores para configuraciones MikroTik
 */

/**
 * Valida configuración del router
 * @param {object} router - Configuración del router
 * @throws {Error} Si la configuración es inválida
 */
export function validateRouter(router) {
  if (!router) {
    throw new Error('Configuración del router es requerida');
  }
  
  if (!router.nombre || router.nombre.trim() === '') {
    throw new Error('Nombre del router es requerido');
  }
  
  if (!router.subnetLan) {
    throw new Error('Subnet LAN es requerida');
  }
  
  // Validar formato CIDR
  const cidrPattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/;
  if (!cidrPattern.test(router.subnetLan)) {
    throw new Error('Subnet LAN debe estar en formato CIDR (ej: 192.168.1.0/24)');
  }
  
  if (!router.dnsServers || router.dnsServers.length === 0) {
    throw new Error('Al menos un servidor DNS es requerido');
  }
  
  if (!router.zonaHoraria) {
    throw new Error('Zona horaria es requerida');
  }
}

/**
 * Valida configuración de WAN
 * @param {object} wan - Configuración WAN
 * @param {number} index - Índice de la WAN
 * @throws {Error} Si la configuración es inválida
 */
export function validateWan(wan, index) {
  if (!wan) {
    throw new Error(`WAN ${index}: Configuración requerida`);
  }
  
  if (!wan.puerto) {
    throw new Error(`WAN ${index}: Puerto es requerido`);
  }
  
  if (!wan.tipo || !['fijo', 'dhcp'].includes(wan.tipo)) {
    throw new Error(`WAN ${index}: Tipo debe ser 'fijo' o 'dhcp'`);
  }
  
  if (wan.tipo === 'fijo') {
    if (!wan.ip) {
      throw new Error(`WAN ${index}: IP es requerida para tipo fijo`);
    }
    if (!wan.gateway) {
      throw new Error(`WAN ${index}: Gateway es requerido para tipo fijo`);
    }
    
    // Validar formato IP
    const ipPattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
    if (!ipPattern.test(wan.ip)) {
      throw new Error(`WAN ${index}: IP inválida`);
    }
    if (!ipPattern.test(wan.gateway)) {
      throw new Error(`WAN ${index}: Gateway inválido`);
    }
  }
}

/**
 * Valida configuración de balanceador
 * @param {object} config - Configuración completa
 * @throws {Error} Si la configuración es inválida
 */
export function validateBalanceadorConfig(config) {
  validateRouter(config.router);
  
  if (!config.wans || config.wans.length === 0) {
    throw new Error('Debe haber al menos 1 WAN');
  }
  
  if (config.wans.length > 4) {
    throw new Error('Máximo 4 WANs permitidas');
  }
  
  config.wans.forEach((wan, index) => {
    validateWan(wan, index + 1);
  });
  
  if (!config.tipo || !['nth', 'pcc'].includes(config.tipo)) {
    throw new Error('Tipo de balanceo debe ser "nth" o "pcc"');
  }
  
  if (!config.routerosVersion || !['v6', 'v7'].includes(config.routerosVersion)) {
    throw new Error('Versión de RouterOS debe ser "v6" o "v7"');
  }
}

/**
 * Valida configuración de hotspot
 * @param {object} config - Configuración completa
 * @throws {Error} Si la configuración es inválida
 */
export function validateHotspotConfig(config) {
  validateRouter(config.router);
  
  // El nombre del hotspot viene de config.router.nombre
  // No es necesario validar nombreHotspot por separado
  
  if (!config.puertosLan || config.puertosLan < 1) {
    throw new Error('Al menos 1 puerto LAN es requerido');
  }
  
  // Validar WiFi si está habilitado
  if (config.wifi && config.wifi.tieneWifi && config.wifi.activar) {
    if (!config.wifi.ssid || config.wifi.ssid.trim() === '') {
      throw new Error('SSID del WiFi es requerido');
    }
  }
  
  // Validar admin (opcionales pero si se proporcionan, deben ser válidos)
  if (config.adminUsername && config.adminUsername.trim() === '') {
    throw new Error('Username del admin no puede estar vacío');
  }
  
  if (config.adminPassword && config.adminPassword.length < 4) {
    throw new Error('Password del admin debe tener al menos 4 caracteres');
  }
  
  // Validar WAN
  if (!config.wan) {
    throw new Error('Configuración WAN es requerida');
  }
  
  if (!config.wan.puerto) {
    throw new Error('Puerto WAN es requerido');
  }
}

/**
 * Valida configuración de failover
 * @param {object} config - Configuración completa
 * @throws {Error} Si la configuración es inválida
 */
export function validateFailoverConfig(config) {
  validateRouter(config.router);
  
  if (!config.wans || config.wans.length === 0) {
    throw new Error('Debe haber al menos 1 WAN');
  }
  
  if (config.wans.length > 3) {
    throw new Error('Máximo 3 WANs permitidas para failover');
  }
  
  config.wans.forEach((wan, index) => {
    validateWan(wan, index + 1);
    
    if (!wan.prioridad || wan.prioridad < 1 || wan.prioridad > 3) {
      throw new Error(`WAN ${index + 1}: Prioridad debe ser 1, 2 o 3`);
    }
    
    if (!wan.checkGateway) {
      throw new Error(`WAN ${index + 1}: IP para check gateway es requerida`);
    }
  });
  
  // Verificar que no haya prioridades duplicadas
  const prioridades = config.wans.map(w => w.prioridad);
  const duplicados = prioridades.filter((p, i) => prioridades.indexOf(p) !== i);
  if (duplicados.length > 0) {
    throw new Error('No puede haber prioridades duplicadas');
  }
}

/**
 * Valida configuración de QoS
 * @param {object} config - Configuración completa
 * @throws {Error} Si la configuración es inválida
 */
export function validateQoSConfig(config) {
  if (!config.tipo || !['simple-queue', 'queue-tree'].includes(config.tipo)) {
    throw new Error('Tipo de QoS debe ser "simple-queue" o "queue-tree"');
  }
  
  // Validate router configuration
  if (!config.router) {
    throw new Error('Configuración del router es requerida');
  }
  
  if (!config.router.nombre || config.router.nombre.trim() === '') {
    throw new Error('Nombre del router es requerido');
  }
  
  // Validate client groups
  if (!config.gruposClientes || config.gruposClientes.length === 0) {
    throw new Error('Debe haber al menos 1 grupo de clientes');
  }
  
  config.gruposClientes.forEach((grupo, index) => {
    if (!grupo.nombre || grupo.nombre.trim() === '') {
      throw new Error(`Grupo ${index + 1}: Nombre es requerido`);
    }
    
    if (!grupo.valor || grupo.valor.trim() === '') {
      throw new Error(`Grupo ${index + 1}: Valor (IP/rango) es requerido`);
    }
  });
  
  // Validate service rules
  if (!config.reglasServicios || config.reglasServicios.length === 0) {
    throw new Error('Debe haber al menos 1 regla de servicio');
  }
  
  config.reglasServicios.forEach((regla, index) => {
    if (!regla.servicio) {
      throw new Error(`Regla ${index + 1}: Servicio es requerido`);
    }
    
    if (!regla.accion || !['limitar', 'bloquear', 'priorizar'].includes(regla.accion)) {
      throw new Error(`Regla ${index + 1}: Acción debe ser "limitar", "bloquear" o "priorizar"`);
    }
    
    if (regla.accion === 'limitar') {
      if (!regla.limite_subida || !regla.limite_bajada) {
        throw new Error(`Regla ${index + 1}: Límites de subida y bajada son requeridos para acción "limitar"`);
      }
    }
    
    if (regla.accion === 'priorizar') {
      if (!regla.prioridad || regla.prioridad < 1 || regla.prioridad > 8) {
        throw new Error(`Regla ${index + 1}: Prioridad debe estar entre 1 y 8 para acción "priorizar"`);
      }
    }
  });
}

/**
 * Valida configuración de portal cautivo
 * @param {object} config - Configuración completa
 * @throws {Error} Si la configuración es inválida
 */
export function validatePortalConfig(config) {
  if (!config.nombreEmpresa) {
    throw new Error('Nombre de la empresa es requerido');
  }
  
  if (!config.colorPrimario) {
    throw new Error('Color primario es requerido');
  }
  
  if (!config.colorSecundario) {
    throw new Error('Color secundario es requerido');
  }
  
  // Validar formato de color hexadecimal
  const colorPattern = /^#[0-9A-Fa-f]{6}$/;
  if (!colorPattern.test(config.colorPrimario)) {
    throw new Error('Color primario debe estar en formato hexadecimal (#RRGGBB)');
  }
  if (!colorPattern.test(config.colorSecundario)) {
    throw new Error('Color secundario debe estar en formato hexadecimal (#RRGGBB)');
  }
}

/**
 * Sanitiza texto para uso en scripts
 * @param {string} text - Texto a sanitizar
 * @returns {string} Texto sanitizado
 */
export function sanitizeText(text) {
  if (!text) return '';
  return text.replace(/["'\\]/g, '').trim();
}

/**
 * Valida que un valor esté en un rango
 * @param {number} value - Valor a validar
 * @param {number} min - Mínimo
 * @param {number} max - Máximo
 * @param {string} fieldName - Nombre del campo
 * @throws {Error} Si está fuera de rango
 */
export function validateRange(value, min, max, fieldName) {
  if (value < min || value > max) {
    throw new Error(`${fieldName} debe estar entre ${min} y ${max}`);
  }
}
